import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { api, formatApiError, imgSrc } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { THEMES, brl, toNum, onlyDigits } from "./theme";
import { ShippingStep } from "./ShippingStep";
import { CustomerStep } from "./CustomerStep";
import { PhotoPicker } from "./PhotoPicker";

const EMPTY_ADDR = { street: "", number: "", complement: "", district: "", city: "", state: "", ibge: "" };

export const CheckoutDialog = ({ product, light = false, customize = false, onClose }) => {
  const t = THEMES[light ? "light" : "dark"];
  const [qty, setQty] = useState(1);
  const [cep, setCep] = useState("");
  const [options, setOptions] = useState([]);
  const [service, setService] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteErr, setQuoteErr] = useState("");
  const [c, setC] = useState({ name: "", email: "", phone: "", cpf: "" });
  const [a, setA] = useState(EMPTY_ADDR);
  const [photo, setPhoto] = useState("");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState("");
  const [payment, setPayment] = useState("online");
  const isMoto = service?.service_id === 0;
  const cash = isMoto && payment === "cash";

  const quote = async (q = qty) => {
    setQuoting(true); setQuoteErr(""); setOptions([]); setService(null);
    try {
      const digits = onlyDigits(cep);
      const [{ data }, via] = await Promise.all([
        api.post("/shipping/quote", { product_id: product.id, quantity: q, cep: digits }),
        fetch(`https://viacep.com.br/ws/${digits}/json/`).then((r) => r.json()).catch(() => ({})),
      ]);
      const opts = Array.isArray(data?.options) ? data.options : [];
      setOptions(opts);
      setService(opts[0] ?? null);
      if (via && !via.erro) setA((s) => ({ ...s, street: s.street || via.logradouro || "", district: s.district || via.bairro || "", city: via.localidade || "", state: via.uf || "", ibge: via.ibge || "" }));
    } catch (err) {
      setQuoteErr(formatApiError(err.response?.data?.detail));
    } finally { setQuoting(false); }
  };

  const changeQty = (n) => { const q = Math.min(20, Math.max(1, n)); setQty(q); if (options.length) quote(q); };

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true); setPayErr("");
    try {
      const { data } = await api.post("/checkout", {
        product_id: product.id, quantity: qty, cep: onlyDigits(cep), service_id: service.service_id, payment: cash ? "cash" : "online",
        customer: c, address: a, customization: { note, photo_url: photo ? imgSrc(photo) : "" }, origin: window.location.origin,
      });
      window.location.assign(data.checkout_url || `/pedido/${data.order_id}`);
    } catch (err) {
      setPayErr(formatApiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const subtotal = toNum(product.price) * qty;
  const total = subtotal + (service ? Number(service.price) : 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog" className={`max-h-[92vh] overflow-y-auto sm:max-w-lg ${t.panel}`}>
        <DialogHeader className="text-left">
          <div className="flex items-center gap-4">
            <img src={imgSrc(product.image)} alt="" className="h-16 w-16 rounded-md object-cover" />
            <div>
              <DialogTitle className={t.title}>{product.name}</DialogTitle>
              <DialogDescription className={t.muted}>{brl(toNum(product.price))} cada · Pix ou cartão em até 5x</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={pay} className="space-y-6">
          <div className="flex items-center justify-between">
            <p className={t.label}>QUANTIDADE</p>
            <div className={`flex items-center gap-1 rounded-full border ${t.divider}`}>
              <button type="button" data-testid="checkout-qty-minus" onClick={() => changeQty(qty - 1)} className="h-9 w-9 text-lg">−</button>
              <span data-testid="checkout-qty" className="w-8 text-center font-semibold">{qty}</span>
              <button type="button" data-testid="checkout-qty-plus" onClick={() => changeQty(qty + 1)} className="h-9 w-9 text-lg">+</button>
            </div>
          </div>
          {customize && (
            <div className="space-y-3">
              <p className={t.label}>PERSONALIZAÇÃO</p>
              <PhotoPicker value={photo} onChange={setPhoto} onBusy={setUploading} />
              <textarea data-testid="checkout-note-input" rows={2} className={t.field} placeholder="Frase, nome ou ideia para a arte (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          )}
          <ShippingStep t={t} cep={cep} setCep={setCep} onQuote={() => quote()} loading={quoting} options={options} service={service} setService={setService} error={quoteErr} />
          {isMoto && (
            <div className="space-y-2" data-testid="checkout-payment-options">
              <p className={t.label}>PAGAMENTO</p>
              {[["online", "Pix ou cartão (online)"], ["cash", "Em espécie (dinheiro) na retirada"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`checkout-payment-${v}`} onClick={() => setPayment(v)} className={`w-full rounded-lg border p-3 text-left text-sm transition-colors ${payment === v ? t.sel : t.opt}`}>{l}</button>
              ))}
            </div>
          )}
          {service && <CustomerStep t={t} c={c} setC={setC} a={a} setA={setA} />}
          <div className={`space-y-1 border-t pt-4 text-sm ${t.divider}`}>
            <p className="flex justify-between"><span className={t.muted}>Produtos</span><span>{brl(subtotal)}</span></p>
            <p className="flex justify-between"><span className={t.muted}>Frete</span><span>{isMoto ? "A combinar" : service ? brl(service.price) : "—"}</span></p>
            <p className="flex justify-between pt-1 text-lg font-bold"><span>Total</span><span data-testid="checkout-total">{brl(total)}</span></p>
          </div>
          {payErr && <p data-testid="checkout-pay-error" className="text-sm text-red-500">{payErr}</p>}
          <button data-testid="checkout-pay-button" type="submit" disabled={!service || paying || uploading} className={`flex w-full items-center justify-center gap-2 py-4 text-sm disabled:opacity-50 ${t.btn}`}>
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {paying ? "Enviando..." : cash ? "Confirmar pedido" : "Ir para o pagamento"}
          </button>
          <p className={`text-center text-xs ${t.muted}`}>{cash ? "Você paga em dinheiro na retirada. Combinamos o moto táxi pelo WhatsApp." : "Você será levado ao ambiente seguro do Asaas para pagar com Pix ou cartão."}</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
